import { groupBy } from "ramda";
import { type ReactElement } from "react";
import { type CardGridArea } from "widgets";

import { type AttributeValue, type Classification, type Parent, type ParentType, type Sample } from "../graphql.js";
import { type ATTRIBUTE_TYPE, ATTRIBUTE_TYPE_OPTIONS } from "../settings/settings.js";
import { OntologyListCard } from "../ui-modules/entities/cards/OntologyListCard.js";
import { SampleParentsCard } from "../ui-modules/entities/cards/SampleParentsCard.js";
import { type IAttributeValues } from "../ui-modules/entities/samples/SampleDataView.js";
import { type ISelectOption } from "./form.js";
import { getPrimaryParentType } from "./sample.js";

export const createSummaryCardsFromClassifications = (
  allClassifications: Classification[],
  area: CardGridArea,
): ReactElement[] => {
  const groupedClassifications = groupBy((classification: Classification) =>
    classification.groupName ? classification.groupName : "Classification Schemes",
  )(allClassifications ?? []);

  if (!groupedClassifications || Object.keys(groupedClassifications).length === 0) {
    return [<OntologyListCard key={`ontology-list-card-0`} classifications={[]} area={area} />];
  }

  return Object.keys(groupedClassifications).map((classificationGroupName: string, index: number) => (
    <OntologyListCard
      key={`ontology-list-card-${index}`}
      title={classificationGroupName}
      classifications={groupedClassifications[classificationGroupName]}
      area={area}
    />
  ));
};

export const buildParentCards = (sample: Sample, area: CardGridArea): ReactElement[] => {
  const primaryParentType: ParentType = getPrimaryParentType(sample.type);

  // reorder the parents to that the primary parent comes first
  sample.parents.sort((_parentA: Parent, parentB: Parent) => {
    if (parentB.parentType.id === primaryParentType.id) {
      return 1;
    }
  });

  return sample.parents.map((sampleParent: Parent) => (
    <SampleParentsCard key={"svc-saa-" + sampleParent.id} parent={sampleParent} area={area} />
  ));
};

/**
 * @todo Looks like it uses only a part of the Interface, which is not precise enoughS
 */
export const mockAttributeValue = (id, attributeType: ATTRIBUTE_TYPE, name, value, valueField): IAttributeValues => {
  const attributeTypeOption: ISelectOption = ATTRIBUTE_TYPE_OPTIONS.find(
    (typeOption) => typeOption.value === attributeType,
  );
  // mock an IAttributeValue: it shall pass getAttributeValue and buildAttributeValueCardRows
  const att: AttributeValue = {
    attribute: {
      id: id,
      attributeName: name,
      groupName: null,
      attributeType: { id: attributeTypeOption.value, name: attributeTypeOption.label },
    },
  } as AttributeValue;

  att[valueField] = value;

  return [att];
};
